/**
 * MoveBar - Viewport-anchored bar during aircraft drag
 *
 * Fixed at bottom-center of the screen. Shows:
 * - Tail number + type code
 * - From area/unit → To area/unit (live detection)
 * - Collision alert (if any)
 * - Save / Revert buttons
 *
 * Visible only when cardState.mode === 'moveBar'
 */

import { useMemo } from 'react'
import { Save, RotateCcw, AlertTriangle, MoveHorizontal } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { useAircraftCardStore } from '@/stores/aircraftCardStore'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import { useCollisionStore } from '@/stores/collisionStore'
import { enrichDetectionWithNames } from '@/lib/utils/format-aircraft-label'
import type { AllocationEnrichedData } from '@/types/aircraft-card'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'

interface MoveBarProps {
  enrichedData: Map<string, AllocationEnrichedData>
  buildings: Building3DConfigExtended[]
  onSaveClick?: (allocationId: string) => void
  onRevertClick?: (allocationId: string) => void
}

export function MoveBar({ enrichedData, buildings, onSaveClick, onRevertClick }: MoveBarProps) {
  const cardMode = useAircraftCardStore((s) => s.cardState.mode)

  const selectedAllocationId = useAircraftEditorStore((s) => s.selectedAllocationId)
  const editedAllocation = useAircraftEditorStore((s) =>
    s.selectedAllocationId ? s.getEditedAllocation(s.selectedAllocationId) : undefined
  )
  const currentDetection = useAircraftEditorStore((s) =>
    s.selectedAllocationId ? s.currentDetections.get(s.selectedAllocationId) : undefined
  )
  const isDirty = useAircraftEditorStore((s) =>
    s.selectedAllocationId ? s.isDirty(s.selectedAllocationId) : false
  )

  const isColliding = useCollisionStore((s) =>
    selectedAllocationId ? s.collidingAircraftIds.has(selectedAllocationId) : false
  )

  // Get enriched data for display
  const data = selectedAllocationId ? enrichedData.get(selectedAllocationId) : undefined

  // Compute from → to labels
  const { fromLabel, toLabel, changed } = useMemo(() => {
    if (!editedAllocation || !buildings) {
      return { fromLabel: '', toLabel: '', changed: false }
    }

    const original = enrichDetectionWithNames(
      {
        areaId: editedAllocation.originalAreaId,
        unitId: editedAllocation.originalUnitId,
        positionType: editedAllocation.originalPositionType,
      },
      editedAllocation.originalPosition,
      buildings
    )

    const current = currentDetection
      ? enrichDetectionWithNames(currentDetection, editedAllocation.currentPosition, buildings)
      : original

    const fromLabel = [original.areaName, original.unitCode].filter(Boolean).join(', ')
    const toLabel = [current.areaName, current.unitCode].filter(Boolean).join(', ')
    const changed = original.areaName !== current.areaName || original.unitCode !== current.unitCode

    return { fromLabel, toLabel, changed }
  }, [editedAllocation, currentDetection, buildings])

  // Only visible during moveBar state
  if (cardMode !== 'moveBar' || !selectedAllocationId) return null

  const tailNumber = data?.aircraft.tailNumber || editedAllocation?.tailNumber || 'Unknown'
  const typeCode = data?.aircraft.typeCode || ''

  return (
    <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-30 pointer-events-auto">
      <div className="flex items-center gap-3 px-4 py-2.5 bg-background/95 backdrop-blur-md border rounded-xl shadow-lg">
        {/* Aircraft identity */}
        <div className="flex items-center gap-2">
          <MoveHorizontal className="w-4 h-4 text-muted-foreground" />
          <span className="font-bold text-sm">{tailNumber}</span>
          {typeCode && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
              {typeCode}
            </Badge>
          )}
        </div>

        <div className="w-px h-6 bg-border" />

        {/* From → To */}
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-muted-foreground">{fromLabel || 'Unknown'}</span>
          {changed && (
            <>
              <span className="text-muted-foreground">&rarr;</span>
              <span className="text-green-600 font-medium">{toLabel}</span>
            </>
          )}
        </div>

        {/* Collision alert */}
        {isColliding && (
          <>
            <div className="w-px h-6 bg-border" />
            <div className="flex items-center gap-1 text-red-600 text-xs font-medium">
              <AlertTriangle className="w-3.5 h-3.5" />
              Collision
            </div>
          </>
        )}

        <div className="w-px h-6 bg-border" />

        {/* Save / Revert */}
        <div className="flex items-center gap-1.5">
          <Button
            size="sm"
            variant="default"
            className="h-7 px-3 text-xs bg-green-600 hover:bg-green-700"
            disabled={!isDirty}
            onClick={() => onSaveClick?.(selectedAllocationId)}
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            Save
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-7 px-3 text-xs"
            disabled={!isDirty}
            onClick={() => onRevertClick?.(selectedAllocationId)}
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" />
            Revert
          </Button>
        </div>
      </div>
    </div>
  )
}
